import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { STATUS_META } from "@/lib/constants";
import PaymentSelector from "@/components/PaymentSelector";
import { API } from "@/lib/api";
import { CreditCard, X, RotateCcw, FileText, Navigation } from "lucide-react";
import { toast } from "sonner";
import { formatPickupLocal } from "@/lib/datetime";

export const ACTIVE_STATUSES = ["assigned", "dispatched", "accepted_by_driver", "en_route", "on_location", "customer_in_car", "in_progress"];

export default function TripCard({ t, onChanged }) {
  const nav = useNavigate();
  const meta = STATUS_META[t.status] || STATUS_META.new;
  const canTrack = ACTIVE_STATUSES.includes(t.status);
  const canRebook = ["completed", "cancelled"].includes(t.status);
  const isPaid = ["paid", "balance_due"].includes(t.payment_status);
  const canPay = t.payment_status !== "paid" && t.payment_status !== "pending" && t.status !== "cancelled";
  const [showPay, setShowPay] = useState(false);
  const contact = t.customer_email || t.customer_phone || "";
  const receiptHref = `${API}/bookings/${t.confirmation_code}/receipt.pdf?contact=${encodeURIComponent(contact)}`;

  const rebook = () => {
    const q = new URLSearchParams({
      rebook: "1",
      trip_type: t.trip_type || "point_to_point",
      pickup: t.pickup_address || "",
      dropoff: t.dropoff_address || "",
      pax: String(t.pax || 1),
      luggage: String(t.luggage || 0),
    });
    if (t.trip_type === "hourly" && t.hours) q.set("hours", String(t.hours));
    nav(`/book?${q.toString()}`);
  };

  const payLabel = t.payment_status === "paid" ? "Paid"
    : t.payment_status === "balance_due" ? `Balance due $${((t.balance_due_cents || 0) / 100).toFixed(2)}`
    : t.payment_status === "pending" ? "Pending" : "Pay later";

  return (
    <div data-testid={`trip-row-${t.booking_id}`} className="border border-white/10 bg-[#0A0A0A] p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-5">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <span className="overline">{t.confirmation_code}</span>
          <span className="status-pill" style={{ color: meta.color }} data-testid={`trip-status-${t.booking_id}`}>{meta.label}</span>
        </div>
        <div className="font-serif text-xl md:text-2xl mb-1">{t.vehicle_name}</div>
        <div className="text-sm text-[#A1A1AA] break-words">{t.pickup_address} {t.dropoff_address && `→ ${t.dropoff_address}`}</div>
        <div className="text-xs text-[#71717A] mt-1">{formatPickupLocal(t.pickup_datetime)} · {t.pax} pax · {t.luggage} bags{t.trip_type === "hourly" && t.hours ? ` · ${t.hours} hr` : ""}</div>
        {t.assigned_driver_name && <div className="text-xs text-[#D4AF37] mt-2 uppercase tracking-[0.18em]">Chauffeur · {t.assigned_driver_name}</div>}
      </div>
      <div className="md:text-right flex md:block items-center justify-between gap-3 md:space-y-2">
        <div>
          <div className="font-serif text-2xl text-[#D4AF37]">${Number(t.fare || 0).toFixed(2)}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]" data-testid={`trip-payment-${t.booking_id}`}>{payLabel}</div>
        </div>
        <div className="flex md:flex-col gap-2 flex-wrap justify-end">
          {canPay && (
            <button data-testid={`pay-now-${t.booking_id}`} onClick={() => setShowPay(true)}
              className="btn-gold !py-2 !px-4 text-[10px] justify-center"><CreditCard size={12}/>Pay now</button>
          )}
          {isPaid && (
            <a data-testid={`receipt-link-${t.booking_id}`} href={receiptHref} target="_blank" rel="noreferrer"
              className="btn-ghost !py-2 !px-4 text-[10px] justify-center inline-flex items-center gap-1.5"><FileText size={12}/>Receipt</a>
          )}
          {canTrack && (
            <a data-testid={`track-link-${t.booking_id}`}
              href={`/track?code=${t.confirmation_code}&email=${encodeURIComponent(t.customer_email || "")}`}
              className="btn-ghost !py-2 !px-4 text-[10px] justify-center inline-flex items-center gap-1.5"><Navigation size={12}/>Track</a>
          )}
          {canRebook && (
            <button data-testid={`rebook-btn-${t.booking_id}`} onClick={rebook}
              className="btn-ghost !py-2 !px-4 text-[10px] justify-center"><RotateCcw size={12}/>Book again</button>
          )}
        </div>
      </div>

      {showPay && (
        <div data-testid={`pay-modal-${t.booking_id}`}
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowPay(false)}>
          <div className="bg-[#050505] border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 relative text-left"
            onClick={(e) => e.stopPropagation()}>
            <button data-testid={`pay-modal-close-${t.booking_id}`} onClick={() => setShowPay(false)}
              className="absolute top-4 right-4 text-[#A1A1AA] hover:text-white" aria-label="Close"><X size={18}/></button>
            <div className="overline mb-1">Pay for your ride</div>
            <div className="font-serif text-2xl text-[#D4AF37] mb-4">{t.confirmation_code}</div>
            <PaymentSelector booking={t}
              onPaid={() => { setShowPay(false); onChanged && onChanged(); toast.success("Payment updated"); }}/>
          </div>
        </div>
      )}
    </div>
  );
}
